import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { endpoints, api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import ChatPanel from "@/components/ChatPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowLeft, Download, Play, RefreshCw } from "lucide-react";
import OverviewTab from "@/pages/tabs/OverviewTab";
import PlanTab from "@/pages/tabs/PlanTab";
import EdaTab from "@/pages/tabs/EdaTab";
import PreprocessingTab from "@/pages/tabs/PreprocessingTab";
import TrainingTab from "@/pages/tabs/TrainingTab";
import ComparisonTab from "@/pages/tabs/ComparisonTab";
import EvaluationTab from "@/pages/tabs/EvaluationTab";
import TuningTab from "@/pages/tabs/TuningTab";
import ExplainTab from "@/pages/tabs/ExplainTab";
import ReportTab from "@/pages/tabs/ReportTab";

const STATUS_COLOR = {
  created: "bg-muted",
  uploaded: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  analyzing: "bg-accent/15 text-accent border-accent/30 animate-pulse",
  training: "bg-accent/15 text-accent border-accent/30 animate-pulse",
  complete: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/15 text-red-400 border-red-500/30",
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "plan", label: "Plan" },
  { id: "eda", label: "EDA" },
  { id: "preprocess", label: "Preprocessing" },
  { id: "training", label: "Training" },
  { id: "compare", label: "Comparison" },
  { id: "tuning", label: "Tuning" },
  { id: "evaluate", label: "Evaluation" },
  { id: "explain", label: "Explain" },
  { id: "report", label: "Report" },
];

export default function ProjectWorkspace() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [status, setStatus] = useState(null);

  const refresh = async () => {
    try {
      const [{ data: p }, { data: s }] = await Promise.all([
        endpoints.getProject(id),
        endpoints.trainingStatus(id),
      ]);
      setProject(p);
      setStatus(s);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { 
    refresh(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!status) return;
    if (status.status === "analyzing" || status.status === "training") {
      const t = setInterval(refresh, 2000);
      return () => clearInterval(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.status]);

  const retrain = async () => {
    try {
      await endpoints.startTraining(id, null);
      toast.success("Retraining started");
      refresh();
    } catch (e) {
      toast.error("Failed to start training");
    }
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/projects/${id}/download/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      const filename = res.headers?.["content-disposition"]?.match(/filename="?([^";]+)/)?.[1]
        || `${project.name}-${kind}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (!project) return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-10 text-muted-foreground">Loading…</div>
    </div>
  );

  const st = project.status;
  const isRunning = st === "analyzing" || st === "training";

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="border-b border-border px-8 py-5">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground text-sm inline-flex items-center gap-1" data-testid="back-dashboard">
              <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
            </Link>
            <Badge className={`text-[10px] font-mono uppercase tracking-wider ${STATUS_COLOR[st] || ""}`}>
              {st}
            </Badge>
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-display font-bold text-3xl tracking-tighter">{project.name}</h1>
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span>File: <span className="font-mono">{project.dataset_filename || "—"}</span></span>
                {project.target_column && <span>Target: <span className="font-mono text-accent">{project.target_column}</span></span>}
                {project.problem_type && <span>Type: <span className="font-mono">{project.problem_type}</span></span>}
                {project.best_model_name && <span>Best: <span className="font-mono text-accent">{project.best_model_name}</span></span>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={refresh} data-testid="refresh-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
              {st !== "created" && (
                <Button size="sm" onClick={retrain} disabled={isRunning} data-testid="retrain-btn" className="bg-accent text-accent-foreground hover:bg-accent/90">
                  <Play className="w-3.5 h-3.5 mr-1.5" /> {isRunning ? "Running…" : "Retrain"}
                </Button>
              )}
              {st === "complete" && (
                <>
                  <Button variant="outline" size="sm" onClick={() => download("pipeline")} data-testid="download-pipeline">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Pipeline
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="download-pdf">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => download("report")} data-testid="download-report">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> HTML
                  </Button>
                </>
              )}
            </div>
          </div>
          {isRunning && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="label-mono">PIPELINE PROGRESS</span>
                <span className="num text-accent">{status?.progress || 0}%</span>
              </div>
              <Progress value={status?.progress || 0} data-testid="training-progress" />
            </div>
          )}
          {st === "failed" && project.error_message && (
            <div className="mt-3 text-xs border border-destructive/30 bg-destructive/5 text-destructive p-3 rounded-sm font-mono whitespace-pre-wrap max-h-32 overflow-auto">
              {project.error_message.split("\n").slice(0, 4).join("\n")}
            </div>
          )}
        </div>

        {/* Tabs + Chat */}
        <div className="flex flex-1 min-h-0">
          <div className="flex-1 min-w-0 overflow-y-auto">
            <Tabs defaultValue="overview" className="w-full">
              <div className="sticky top-0 z-10 glass border-b border-border">
                <TabsList className="h-12 rounded-none w-full justify-start bg-transparent px-8 gap-1 overflow-x-auto">
                  {TABS.map((t) => (
                    <TabsTrigger
                      key={t.id}
                      value={t.id}
                      data-testid={`tab-${t.id}`}
                      className="text-xs data-[state=active]:bg-secondary data-[state=active]:text-foreground rounded-sm"
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
              <div className="p-8 max-w-[1200px]">
                <TabsContent value="overview"><OverviewTab id={id} status={status} /></TabsContent>
                <TabsContent value="plan"><PlanTab id={id} /></TabsContent>
                <TabsContent value="eda"><EdaTab id={id} /></TabsContent>
                <TabsContent value="preprocess"><PreprocessingTab id={id} /></TabsContent>
                <TabsContent value="training"><TrainingTab id={id} status={status} /></TabsContent>
                <TabsContent value="compare"><ComparisonTab id={id} /></TabsContent>
                <TabsContent value="tuning"><TuningTab id={id} /></TabsContent>
                <TabsContent value="evaluate"><EvaluationTab id={id} /></TabsContent>
                <TabsContent value="explain"><ExplainTab id={id} /></TabsContent>
                <TabsContent value="report"><ReportTab id={id} project={project} onDownload={download} /></TabsContent>
              </div>
            </Tabs>
          </div>
          <div className="hidden xl:block w-[340px] shrink-0">
            <div className="sticky top-0 h-screen">
              <ChatPanel projectId={id} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
